import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import { SignalElement, inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import { NotificationService } from "ui-components/notification/notification-service.ts";
import "ui-components/form-elements/switch/switch";

import { getAssetUrl } from "helpers/assetHelpers";
import { t, translate } from "../../translations/i18n";
import { communicationPreferencesStyles } from "./communication-preferences.css.ts";

interface FollowedCreator {
  id: string;
  handle: string | null;
  profileImage: string | null;
  profileImagePath: string | null;
}

@customElement("communication-preferences")
export class CommunicationPreferences extends SignalElement {
  static override styles = communicationPreferencesStyles;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @state()
  private creators: FollowedCreator[] = [];

  @state()
  private mutedCreatorIds = new Set<string>();

  @state()
  private isLoading = true;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.isLoading = true;
    try {
      const [creatorsResult, mutedResult] = await Promise.all([
        this.rpcService.call<{ influencers?: FollowedCreator[]; totalCount?: number; hasMore?: boolean }>(
          "get-fan-creators",
          { limit: 100, offset: 0 },
        ),
        this.rpcService.call<{ mutedCreatorIds?: string[] }>("get-muted-creators", {}),
      ]);

      if (creatorsResult?.influencers) {
        this.creators = creatorsResult.influencers;
      }

      if (mutedResult?.mutedCreatorIds) {
        this.mutedCreatorIds = new Set(mutedResult.mutedCreatorIds);
      }
    } catch (error) {
      console.error("Error loading communication preferences:", error);
      NotificationService.error(translate("accountSettings.communicationPreferences.errorLoading"));
    } finally {
      this.isLoading = false;
    }
  }

  private async handleToggle(creatorId: string, currentlyMuted: boolean) {
    const previousMuted = new Set(this.mutedCreatorIds);

    const newMuted = new Set(this.mutedCreatorIds);
    if (currentlyMuted) {
      newMuted.delete(creatorId);
    } else {
      newMuted.add(creatorId);
    }
    this.mutedCreatorIds = newMuted;

    try {
      const rpcName = currentlyMuted ? "unmute-creator" : "mute-creator";
      const result = await this.rpcService.call<{ success?: boolean } | undefined>(rpcName, { creatorId });

      if (result && !result.success) {
        this.mutedCreatorIds = previousMuted;
        NotificationService.error(translate("accountSettings.communicationPreferences.errorUpdating"));
      }
    } catch {
      this.mutedCreatorIds = previousMuted;
      NotificationService.error("Failed to update preference");
    }
  }

  override render() {
    if (this.isLoading) {
      return html`<div class="loading">${t("accountSettings.communicationPreferences.loading")}</div>`;
    }

    if (this.creators.length === 0) {
      return html`<div class="empty-state">${t("accountSettings.communicationPreferences.emptyState")}</div>`;
    }

    return html`
      <div class="communication-preferences">
        <p class="description">${t("accountSettings.communicationPreferences.description")}</p>
        <div class="creator-list">
          ${repeat(
            this.creators,
            (c) => c.id,
            (creator) => {
              const isMuted = this.mutedCreatorIds.has(creator.id);
              const rawImagePath = creator.profileImagePath ?? creator.profileImage;
              const imageSrc = rawImagePath ? getAssetUrl(rawImagePath) : undefined;

              return html`
                <div class="creator-row">
                  ${imageSrc
                    ? html`<img class="creator-avatar" src=${imageSrc} alt=${creator.handle ?? ""} />`
                    : html`<div class="creator-avatar"></div>`}
                  <span class="creator-handle">${creator.handle ?? "Unknown"}</span>
                  <wow-switch
                    ?checked=${!isMuted}
                    data-testid="mute-toggle-${creator.handle}"
                    @change=${(e: CustomEvent) => {
                      const isNowChecked = e.detail.checked;
                      if (isNowChecked && isMuted) {
                        this.handleToggle(creator.id, true);
                      } else if (!isNowChecked && !isMuted) {
                        this.handleToggle(creator.id, false);
                      }
                    }}
                  ></wow-switch>
                </div>
              `;
            },
          )}
        </div>
      </div>
    `;
  }
}
